import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, authHeadersOnly } from '../services/apiConfig';
import './AccountDashboard.css';
import AccountQuickLinks from './AccountQuickLinks';
import RecentOrdersPreview from './RecentOrdersPreview';
import AccountSettingsGrid from './AccountSettingsGrid';
import MyOrders from './MyOrders';
import UserQueries from './UserQueries';

function AccountDashboard({ user, onClose, onShowOrders, onCartClick, openOrders }) {
  const [profile, setProfile]       = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [feedbacks, setFeedbacks]   = useState([]);
  const [reviews, setReviews]       = useState([]);
  const [queries, setQueries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showOrdersModal, setShowOrdersModal] = useState(!!openOrders);
  const [showQueriesModal, setShowQueriesModal] = useState(false);

  useEffect(() => { fetchDashboardData(); }, []);

  useEffect(() => {
    console.log('showQueriesModal changed to:', showQueriesModal);
  }, [showQueriesModal]);

  const fetchDashboardData = async () => {
    try {
      const [profileRes, ordersRes, feedbackRes, reviewsRes, queriesRes] = await Promise.all([
        axios.get(`${API}/api/auth/user/${user.userId}/profile`, { headers: authHeadersOnly() }).catch(() => ({ data: null })),
        axios.get(`${API}/api/orders/user/${user.userId}`, { headers: authHeadersOnly() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/orders/feedback/user/${user.userId}`, { headers: authHeadersOnly() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/orders/reviews/user/${user.userId}`, { headers: authHeadersOnly() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/contact/user/${user.userId}`, { headers: authHeadersOnly() }).catch(() => ({ data: [] }))
      ]);
      setProfile(profileRes.data || { name: user.name });
      setRecentOrders((ordersRes.data || []).slice(0, 3));
      setFeedbacks(feedbackRes.data || []);
      setReviews(reviewsRes.data || []);
      setQueries(queriesRes.data || []);
    } catch {
      setProfile({ name: user.name });
    } finally {
      setLoading(false);
    }
  };

  const handleStartShopping = () => {
    onClose();
    setTimeout(() => {
      document.getElementById('standard')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleContactSupport = () => {
    onClose();
    setTimeout(() => {
      const footer = document.getElementById('footer') || document.querySelector('footer') || document.querySelector('.contact-section');
      if (footer) footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 150);
  };

  if (loading) {
    return (
      <div className="db-loading">
        <div className="db-spin"></div>
        <p>Loading your account…</p>
      </div>
    );
  }

  return (
    <>
      <div className="db-modal">
        <div className="db-overlay" onClick={onClose}></div>
        <div className="db-container">

          {/* Header */}
          <div className="db-header">
            <div className="db-header-left">
              <div className="db-avatar">
                {(profile?.name || user.name || 'U')[0].toUpperCase()}
              </div>
              <div>
                <h2>{profile?.name || user.name}</h2>
                <p>{user.email}</p>
              </div>
            </div>
            <button className="db-close" onClick={onClose}>✕</button>
          </div>

          <div className="db-body">

            <AccountQuickLinks
              onShowOrders={() => setShowOrdersModal(true)}
              onShowQueries={() => setShowQueriesModal(true)}
              onCartClick={onCartClick}
              onClose={onClose}
            />

            <RecentOrdersPreview
              orders={recentOrders}
              onViewAll={() => setShowOrdersModal(true)}
            />

            <AccountSettingsGrid onShowOrders={() => setShowOrdersModal(true)} onClose={onClose} userId={user.userId} />

            {/* Feedback */}
            {feedbacks.length > 0 && (
              <div className="db-section">
                <div className="db-section-hdr">
                  <span className="db-section-title">My Feedback</span>
                  <span className="db-badge db-badge-yellow">{feedbacks.length} review{feedbacks.length > 1 ? 's' : ''}</span>
                </div>
                <div className="db-feedback-list">
                  {feedbacks.map((fb, i) => (
                    <div key={i} className="db-feedback-item">
                      <div className="db-feedback-top">
                        <span className="db-feedback-name">{fb.userName || user.name || 'You'}</span>
                        <span className="db-feedback-date">{new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                      <div className="db-stars">
                        {[1,2,3,4,5].map(s => (
                          <span key={s} style={{ color: s <= fb.rating ? '#ffc107' : '#ddd' }}>★</span>
                        ))}
                        <span className="db-rating-label">{['','Poor','Fair','Good','Very Good','Excellent'][fb.rating]}</span>
                      </div>
                      {fb.comment && <p className="db-feedback-comment">"{fb.comment}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews */}
            {reviews.length > 0 && (
              <div className="db-section">
                <div className="db-section-hdr">
                  <span className="db-section-title">My Product Reviews</span>
                  <span className="db-badge db-badge-yellow">{reviews.length} review{reviews.length > 1 ? 's' : ''}</span>
                </div>
                <div className="db-feedback-list">
                  {reviews.map((rv, i) => (
                    <div key={i} className="db-feedback-item">
                      <div className="db-feedback-top">
                        <span className="db-feedback-name">{rv.userName || user.name || 'You'}</span>
                        <span className="db-feedback-date">{new Date(rv.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                      <div className="db-stars">
                        {[1,2,3,4,5].map(s => (
                          <span key={s} style={{ color: s <= rv.rating ? '#ffc107' : '#ddd' }}>★</span>
                        ))}
                        <span className="db-rating-label">{['','Poor','Fair','Good','Very Good','Excellent'][rv.rating]}</span>
                      </div>
                      {rv.reviewText && <p className="db-feedback-comment">"{rv.reviewText}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

           

            {/* Contact support */}
            <div className="db-support">
              <div className="db-support-text">
                <strong>Need help with your order?</strong>
                <p>Our team is ready to assist you with any queries.</p>
              </div>
              <button className="db-btn-dark" onClick={handleContactSupport}>Contact Support</button>
            </div>

          </div>
        </div>
      </div>

      {showOrdersModal && (
        <MyOrders user={user} onClose={() => setShowOrdersModal(false)} />
      )}

      {showQueriesModal && (
        <UserQueries user={user} onClose={() => setShowQueriesModal(false)} />
      )}
    </>
  );
}

export default AccountDashboard;
